import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Menu, X, ChevronDown, Phone, Mail, Briefcase,
  BookOpen, Users, Image as ImageIcon, Newspaper, HelpCircle,
  Flame, Waves, GitBranch, Droplets, Clock, Filter, HardHat, Hammer, Factory, Handshake, MessageSquare,
} from 'lucide-react';
import Logo from './Logo';
import { company } from '../mock';

const servicesMenu = [
  { icon: Waves, title: 'Drain Cleaning & Repair', desc: 'Clogs & slow drains cleared', to: '/residential/drain-cleaning-savannah' },
  { icon: Flame, title: 'Water Heater Installation & Repair', desc: 'Tank & tankless service', to: '/residential/water-heater-repair-savannah' },
  { icon: Filter, title: 'Water Filtration & Softener Systems', desc: 'Cleaner, softer water', to: '/residential/water-filtration-savannah' },
  { icon: Droplets, title: 'Slab Leak Detection & Repair', desc: 'Leaks under your foundation', to: '/residential/slab-leak-detection-savannah' },
  { icon: GitBranch, title: 'Sewer Line Repair & Replacement', desc: 'Sewer & water line service', to: '/residential/sewer-line-repair-savannah' },
  { icon: Clock, title: 'Same-Day Plumbing Repair', desc: 'Fast help when it can\u2019t wait', to: '/residential/same-day-plumbing-repair-savannah' },
];

const projectsMenu = [
  { icon: HardHat, title: 'New Construction', desc: 'Rough-in to final inspection', to: '/commercial/new-construction' },
  { icon: Hammer, title: 'Large-Scale Renovations', desc: 'Repipes & major remodels', to: '/commercial/large-scale-renovations' },
  { icon: Factory, title: 'Large Plumbing Projects', desc: 'Complex commercial & industrial jobs', to: '/commercial/large-plumbing-projects' },
  { icon: Handshake, title: 'Contractor Services', desc: 'Partnering with builders & GCs', to: '/commercial/contractor-services' },
  { icon: Handshake, title: 'Become a Trade Partner', desc: 'For builders & GCs', to: '/trade-partner' },
];

const aboutMenu = [
  { icon: BookOpen, title: 'Our Story', desc: `Family owned since ${company.founded}`, to: '/about' },
  { icon: Users, title: 'Our Team', desc: 'Meet the people', to: '/team' },
  { icon: ImageIcon, title: 'Photo Gallery', desc: 'Our recent work', to: '/gallery' },
  { icon: Briefcase, title: 'Careers', desc: 'Join our team', to: '/careers' },
];

const contactMenu = [
  { icon: MessageSquare, title: 'Get in Touch', desc: 'Send an inquiry', to: '/contact' },
  { icon: Phone, title: 'Call Us', desc: company.phoneDisplay, href: company.phoneHref },
  { icon: Mail, title: 'Email Us', desc: company.email, href: `mailto:${company.email}` },
];

const resourcesMenu = [
  { icon: Newspaper, title: 'Blog', desc: 'Tips, projects & news', to: '/blog' },
  { icon: HelpCircle, title: 'FAQ', desc: 'Answers before you call', to: '/faq' },
];

function MegaRow({ it }) {
  const I = it.icon;
  const inner = (
    <>
      <span className="mega-ic"><I size={20} strokeWidth={1.6} /></span>
      <span className="mega-text">
        <span className="mega-title">{it.title}</span>
        <span className="mega-desc">{it.desc}</span>
      </span>
    </>
  );
  return it.to
    ? <Link className="mega-item" to={it.to} role="menuitem">{inner}</Link>
    : <a className="mega-item" href={it.href} role="menuitem">{inner}</a>;
}

function Mega({ items }) {
  return (
    <div className="nav-mega" role="menu">
      {items.map((it) => <MegaRow it={it} key={it.title} />)}
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <>
      {/* Utility bar */}
      <div className="utility">
        <div className="wrap">
          <div className="util-left">
            <a className="phone" href={company.phoneHref}><Phone size={13} /> {company.phoneDisplay}</a>
            <span className="util-sep">&bull;</span>
            <span>{company.hours}</span>
          </div>
          <div className="util-right">
            <Link className="hiring-pill" to="/careers" data-testid="utility-hiring-pill">
              <Briefcase size={12} /> WE&rsquo;RE HIRING
            </Link>
            <span className="util-sep">&bull;</span>
            <span className="tag">{company.licenseNumber}</span>
            <span className="util-sep">&bull;</span>
            <span>Family Owned Since {company.founded}</span>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="site">
        <div className="wrap">
          <Link className="brand" to="/">
            <Logo size={56} />
            <span className="name">{company.name}<span>{company.location}</span></span>
          </Link>
          <nav className="primary" aria-label="Primary">
            <Link to="/">Home</Link>
            <div className="nav-drop">
              <span className="nav-drop-trigger">Service <ChevronDown size={14} /></span>
              <Mega items={servicesMenu} />
            </div>
            <div className="nav-drop">
              <span className="nav-drop-trigger">Construction <ChevronDown size={14} /></span>
              <Mega items={projectsMenu} />
            </div>
            <div className="nav-drop">
              <span className="nav-drop-trigger">About <ChevronDown size={14} /></span>
              <Mega items={aboutMenu} />
            </div>
            <Link to="/service-areas">Service Areas</Link>
            <div className="nav-drop">
              <span className="nav-drop-trigger">Resources <ChevronDown size={14} /></span>
              <Mega items={resourcesMenu} />
            </div>
            <div className="nav-drop">
              <span className="nav-drop-trigger">Contact <ChevronDown size={14} /></span>
              <Mega items={contactMenu} />
            </div>
          </nav>
          <div className="header-cta">
            <button className="nav-toggle" aria-label="Toggle menu" onClick={() => setOpen((v) => !v)}>
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
        <div className={`mobile-nav ${open ? 'open' : ''}`}>
          <div className="wrap">
            <Link to="/" onClick={close}>Home</Link>
            <span className="mob-label">Service</span>
            <Link to="/residential/drain-cleaning-savannah" onClick={close}>Drain Cleaning &amp; Repair</Link>
            <Link to="/residential/water-heater-repair-savannah" onClick={close}>Water Heater Installation &amp; Repair</Link>
            <Link to="/residential/water-filtration-savannah" onClick={close}>Water Filtration &amp; Softener Systems</Link>
            <Link to="/residential/slab-leak-detection-savannah" onClick={close}>Slab Leak Detection &amp; Repair</Link>
            <Link to="/residential/sewer-line-repair-savannah" onClick={close}>Sewer Line Repair &amp; Replacement</Link>
            <Link to="/residential/same-day-plumbing-repair-savannah" onClick={close}>Same-Day Plumbing Repair</Link>
            <span className="mob-label">Construction</span>
            <Link to="/commercial/new-construction" onClick={close}>New Construction</Link>
            <Link to="/commercial/large-scale-renovations" onClick={close}>Large-Scale Renovations</Link>
            <Link to="/commercial/large-plumbing-projects" onClick={close}>Large Plumbing Projects</Link>
            <Link to="/commercial/contractor-services" onClick={close}>Contractor Services</Link>
            <Link to="/trade-partner" onClick={close}>Become a Trade Partner</Link>
            <span className="mob-label">About</span>
            <Link to="/about" onClick={close}>Our Story</Link>
            <Link to="/team" onClick={close}>Our Team</Link>
            <Link to="/gallery" onClick={close}>Photo Gallery</Link>
            <Link to="/careers" onClick={close}>Careers</Link>
            <Link to="/service-areas" onClick={close}>Service Areas</Link>
            <span className="mob-label">Resources</span>
            <Link to="/blog" onClick={close}>Blog</Link>
            <Link to="/faq" onClick={close}>FAQ</Link>
            <span className="mob-label">Contact</span>
            <Link to="/contact" onClick={close}>Get in Touch</Link>
            <a href={company.phoneHref} onClick={close}>{company.phoneDisplay}</a>
            <a href={`mailto:${company.email}`} onClick={close}>{company.email}</a>
          </div>
        </div>
      </header>
    </>
  );
}
